'use client';
// CQI-1 — le chaînon entre le contrôle interne et le système qualité.
//
// ── Ce que cet écran fait, et ce qu'il ne fait surtout pas ──────────────────
//
// Il PROPOSE. Il ne crée rien. Une règle de Westgard violée est un signal
// qualité, pas une non-conformité : le moteur qualifie environ un passage sur
// vingt en 1-2s — c'est sa définition, ±2σ recouvre 95,4 % d'une loi normale —
// et ouvrir une fiche à chaque déclenchement produirait plusieurs centaines de
// non-conformités par an sur 126 cartes. Un registre qu'on ne peut plus lire ne
// protège plus personne, et il cesse même de protéger contre les vraies : le
// vrai écart s'y noie.
//
// La décision revient donc au technicien ou à la RQ, AVEC LE CONTEXTE SOUS LES
// YEUX — la règle qui a mordu, les valeurs et leur z, la série récente qui a
// servi au verdict. Sans ce contexte, « accepter » ou « écarter » ne serait pas
// une décision, seulement un clic.
//
// ── Deux issues, deux traces, aucune trace parasite ─────────────────────────
//
// Accepter ouvre une NC du registre COMMUN (`ged-nc`), avec son lien typé et sa
// ligne au journal chaîné. Écarter n'écrit qu'un drapeau, un motif obligatoire
// et une ligne au journal : aucune non-conformité, aucun lien, aucune action.
// Un refus doit être vérifiable en audit — pourquoi ce rejet n'a pas donné de
// NC — sans rien encombrer.
//
// ── Et ce qui a été écarté ? (07/08/2026) ──────────────────────────────────
//
// Écarter est une décision, pas une suppression. Jusqu'à cette date le moteur
// ne servait QUE les propositions ouvertes : une écartée n'apparaissait plus
// nulle part, et la route qui permet de la reprendre était donc inatteignable —
// une erreur de clic condamnait définitivement une proposition. Le panneau
// « Propositions écartées », en bas, les rend consultables et, pour la RQ,
// reprenables. Il est SÉPARÉ de la liste du haut à dessein : une écartée a été
// jugée, elle n'attend pas de décision, et les mêler ferait remonter comme
// « à trancher » des dossiers déjà tranchés.
//
// ── La liste n'est pas un fait, c'est une conséquence ───────────────────────
//
// Elle est DÉRIVÉE à chaque lecture contre la règle de déclenchement en
// vigueur, qui voyage donc avec elle : on ne peut pas juger « trois
// propositions » sans savoir sous quelle règle elles ont été produites. Changer
// le seuil change immédiatement ce que montre cet écran, y compris sur les
// passages saisis il y a un mois.
import React, { useCallback, useEffect, useState } from 'react';
import { fetchEcriture } from '@/lib/ecrire';
import { BoutonJournal } from '@/components/BoutonJournal';
import { useGed, userName } from '@/lib/store';
import { fmtDate } from '@/lib/meta';
import { card, inputStyle, I, EmptyState, useBusy, Selecteur } from '@/components/ui';
import { lbl } from './commun';

import { Bouton } from '@/shared/Bouton';
type Regle = { code: string; gravite: 'alerte' | 'rejet'; niveau: number | null; libelle?: string };
type Proposition = {
  propose: boolean; motif: string;
  gravite: 'mineure' | 'majeure' | 'critique' | null;
  regle: string | null; occurrences: number; fenetre: number; repete: boolean;
  decision: 'acceptee' | 'ecartee' | null;
};
type Ligne = {
  passageId: string; carteId: string; periodeId: string;
  analyte: string; equipement: string; equipementId: string | null; lotControle: string | null;
  date: string; numeroSerie: number | null; statut: 'conforme' | 'alerte' | 'rejet';
  regles: Regle[];
  valeurs: { niveau: number; valeur: number; z: number | null }[];
  historique: { date: string; statut: string; regles: string[] }[];
  proposition: Proposition;
};
/** Une proposition déjà JUGÉE et refusée — avec la décision qui l'a refusée. */
type LigneEcartee = Ligne & { ecartement: { motif: string; par: string; le: string | null } };
export type ParamsNc = {
  actif: boolean;
  surRejet: 'toujours' | 'siRepete' | 'jamais';
  surAlerte: 'toujours' | 'siRepete' | 'jamais';
  fenetreRepetition: number; occurrencesRepetition: number;
  reglesExclues: string[];
  graviteIsolee: 'mineure' | 'majeure' | 'critique';
  graviteRepetee: 'mineure' | 'majeure' | 'critique';
  horizonJours: number; seulementSansSuite: boolean;
};
type Etat = {
  params: ParamsNc; defauts: ParamsNc;
  catalogue: { code: string; libelle?: string; gravite: string }[];
  propositions: Ligne[];
  // Toujours présent depuis le 07/08/2026, vide tant que `?ecartees=1` n'est
  // pas demandé. Un tableau optionnel ne permettrait pas de distinguer
  // « aucune écartée » d'un moteur qui ne sait pas les servir — c'est
  // exactement la confusion qu'il fallait lever.
  ecartees?: LigneEcartee[];
  recapitulatif: { total: number; repetees: number; surRejet: number };
};

const REACTIONS: { v: ParamsNc['surRejet']; l: string }[] = [
  { v: 'toujours', l: 'Proposer à chaque fois' },
  { v: 'siRepete', l: 'Proposer seulement si ça se répète' },
  { v: 'jamais', l: 'Ne jamais proposer' },
];
const GRAVITES = ['mineure', 'majeure', 'critique'] as const;

const chip = (bg: string, fg: string): React.CSSProperties => ({
  fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: bg, color: fg,
});

export function PropositionsNc({ equipementId }: { equipementId?: string }) {
  const { session, showToast } = useGed();
  const estRQ = session.roles.includes('rq');
  const [etat, setEtat] = useState<Etat | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [ouverte, setOuverte] = useState<string | null>(null);
  const [motif, setMotif] = useState('');
  const [reglages, setReglages] = useState(false);
  // Les écartées sont lues À PART, au clic : elles ne sont pas ce qu'on vient
  // faire ici, et la lecture relit toutes les périodes ouvertes.
  const [ecartees, setEcartees] = useState<LigneEcartee[] | null>(null);
  const [panneauEcartees, setPanneauEcartees] = useState(false);
  const [erreurEcartees, setErreurEcartees] = useState<string | null>(null);
  const { run, estOccupe } = useBusy();

  const qs = useCallback((ecarteesAussi: boolean) => {
    const p = new URLSearchParams();
    if (equipementId) p.set('equipement', equipementId);
    if (ecarteesAussi) p.set('ecartees', '1');
    const s = p.toString();
    return s ? `?${s}` : '';
  }, [equipementId]);

  const chargerEcartees = useCallback(async () => {
    setErreurEcartees(null);
    const r = await fetch(`/api/ged/ged/cqi-nc${qs(true)}`, { cache: 'no-store' });
    if (!r.ok) { setErreurEcartees(`Les propositions écartées n’ont pas pu être lues (${r.status}).`); return; }
    const d = await r.json();
    // `undefined` = moteur d'avant le 07/08/2026, qui ne sert pas les écartées.
    // On le DIT, au lieu d'afficher un « aucune » qui serait faux.
    if (!Array.isArray(d?.ecartees)) {
      setErreurEcartees('Ce moteur ne sait pas encore servir les propositions écartées — la liste ci-dessous serait fausse.');
      return;
    }
    setEcartees(d.ecartees as LigneEcartee[]);
  }, [qs]);

  const charger = useCallback(async () => {
    setErreur(null);
    const r = await fetch(`/api/ged/ged/cqi-nc${qs(false)}`, { cache: 'no-store' });
    // Un échec est DIT. Une liste vide silencieuse se lirait « aucun signal
    // qualité ouvert » — le message qu'un écran d'accréditation ne doit jamais
    // fabriquer à partir d'un 403.
    if (!r.ok) { setErreur(`Les propositions n’ont pas pu être lues (${r.status}).`); return; }
    setEtat(await r.json());
  }, [qs]);

  // ⚠️ `setState` synchrone dans un effet est une ERREUR de lint dans ce dépôt
  // (`react-hooks/set-state-in-effect`), et pas seulement une préférence : elle
  // déclenche des rendus en cascade. On repousse donc le chargement d'un tour
  // de boucle, comme le fait déjà `Cqi.tsx`, avec le drapeau qui empêche
  // d'écrire après un changement d'équipement.
  useEffect(() => {
    let vivant = true;
    void Promise.resolve().then(() => (vivant ? charger() : undefined));
    return () => { vivant = false; };
  }, [charger]);

  const decider = async (l: Ligne, acte: 'nc' | 'ecarter') => {
    const url = `/api/ged/ged/cqi-passages/${l.passageId}/nc${acte === 'ecarter' ? '/ecarter' : ''}`;
    const corps = acte === 'ecarter' ? { motif } : { gravite: l.proposition.gravite };
    const r = await fetchEcriture(url, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(corps),
    });
    const d = await r.json().catch(() => null);
    if (!r.ok) { showToast(d?.error?.message || 'Refusé par le moteur.', 'erreur'); return; }
    showToast(acte === 'ecarter'
      ? 'Proposition écartée — aucune non-conformité créée.'
      : `Non-conformité ${d?.ncRef} ouverte.`);
    setOuverte(null); setMotif('');
    await charger();
    // Écarter fait passer la ligne d'une liste à l'autre : si le panneau des
    // écartées est ouvert, il doit la voir arriver, sinon elle « disparaît ».
    if (panneauEcartees) await chargerEcartees();
  };

  /** Remettre une proposition écartée à l'examen. RQ seul, motif obligatoire.
   *
   *  Ce n'est pas défaire un clic : c'est revenir sur une décision qu'un
   *  collègue a signée. Le moteur exige donc un motif d'au moins dix
   *  caractères, comme pour écarter, et l'inscrit au journal de la carte à
   *  côté de l'écartement qu'il lève. */
  const reprendre = async (l: LigneEcartee, motifReprise: string) => {
    const r = await fetchEcriture(`/api/ged/ged/cqi-passages/${l.passageId}/nc/reprendre`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ motif: motifReprise }),
    });
    const d = await r.json().catch(() => null);
    if (!r.ok) { showToast(d?.error?.message || 'Refusé par le moteur.', 'erreur'); return; }
    showToast('Proposition remise à l’examen — elle réapparaît dans la liste du haut si la règle en vigueur la propose encore.');
    await charger();
    await chargerEcartees();
  };

  if (erreur) {
    return (
      <div style={{ ...card, padding: 16, fontSize: 12.5, borderInlineStart: '3px solid var(--red-600)' }}>
        <strong style={{ color: 'var(--red-600)' }}>{erreur}</strong>{' '}
        Ce n’est pas « aucune proposition » : c’est une lecture qui a échoué.
      </div>
    );
  }
  if (!etat) return <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>Lecture des propositions…</p>;

  const p = etat.params;
  const phrase = (r: ParamsNc['surRejet']) => REACTIONS.find((x) => x.v === r)?.l.toLowerCase() || '';

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ ...card, padding: 13, display: 'grid', gap: 8, fontSize: 12.5 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <strong style={{ color: 'var(--text-strong)' }}>
            {etat.recapitulatif.total === 0
              ? 'Aucune non-conformité proposée'
              : `${etat.recapitulatif.total} non-conformité(s) PROPOSÉE(s)`}
          </strong>
          {etat.recapitulatif.repetees > 0 && (
            <span style={chip('var(--red-50)', 'var(--red-600)')}>{etat.recapitulatif.repetees} sur situation répétée</span>
          )}
          <span style={{ flex: 1 }} />
          {/* Le journal du PARAMÉTRAGE, à côté du bouton qui le modifie : ces
                    réglages s'écrivaient au journal sous une clé fixe sans aucun
                    accès à l'écran (audit traçabilité du 24/08/2026). */}
          <BoutonJournal reference="CQI-NC" quoi="des réglages de déclenchement" />
          {estRQ && (
            <Bouton taille="sm" onClick={() => setReglages(!reglages)}>
              <I n="gear" s={13} />{reglages ? 'Masquer le déclenchement' : 'Régler le déclenchement'}
            </Bouton>
          )}
        </div>
        <p style={{ margin: 0, color: 'var(--text-muted)' }}>
          Rien n’est créé automatiquement. Une règle de Westgard violée est un signal, pas
          une non-conformité : un 1-2s est attendu une fois sur vingt par construction, et
          un registre alimenté d’office deviendrait illisible — donc inutile au moment où on
          lui demanderait de servir. La décision reste humaine.
        </p>
        <p style={{ margin: 0, color: 'var(--text-muted)' }}>
          <strong style={{ color: 'var(--text-strong)' }}>Règle en vigueur :</strong>{' '}
          {p.actif
            ? <>sur un <strong>rejet</strong>, {phrase(p.surRejet)} ; sur une <strong>alerte</strong>, {phrase(p.surAlerte)}.
              Une situation est dite répétée à partir de {p.occurrencesRepetition} passages non conformes
              sur les {p.fenetreRepetition} derniers. Gravité proposée : {p.graviteIsolee} si isolée,
              {' '}{p.graviteRepetee} si répétée.
              {p.horizonJours > 0
                ? <> Au-delà de {p.horizonJours} jours, un passage n’est plus proposé — une cause ne se retrouve plus, et une non-conformité sans cause ne protège de rien.</>
                : <> Aucun horizon : tout l’historique est proposé.</>}
              {p.seulementSansSuite
                ? <> Les passages déjà soldés (dérogation, repasse, validation) sont écartés : leur traitement est tracé.</>
                : <> Les passages déjà soldés sont proposés eux aussi.</>}
              {p.reglesExclues.length > 0 && <> Règles écartées du déclenchement : {p.reglesExclues.join(', ')}.</>}</>
            : <>le déclenchement de propositions est <strong>désactivé</strong> — aucune situation ne remontera ici,
              quelle que soit sa gravité.</>}
        </p>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
          Cette liste est recalculée à chaque ouverture contre la règle ci-dessus : la modifier
          change ce qui est proposé sur les passages DÉJÀ saisis, pas seulement sur les suivants.
        </p>
      </div>

      {reglages && estRQ && <Reglages params={p} defauts={etat.defauts} catalogue={etat.catalogue} onEnregistre={charger} />}

      {etat.propositions.length === 0 && (
        <EmptyState
          titre="Aucune proposition ouverte"
          indice={p.actif
            ? 'Aucun passage non conforme n’atteint la règle de déclenchement, ou tous ont déjà été tranchés — acceptés ou écartés.'
            : 'Le déclenchement est désactivé : ce vide ne dit rien de l’état des contrôles.'}
        />
      )}

      <div style={{ display: 'grid', gap: 8 }}>
        {etat.propositions.map((l) => {
          const active = ouverte === l.passageId;
          const rejet = l.statut === 'rejet';
          return (
            <div key={l.passageId} style={{ ...card, padding: 0, overflow: 'hidden', borderInlineStart: `3px solid ${rejet ? 'var(--red-600)' : 'var(--amber-500)'}` }}>
              <button onClick={() => { setOuverte(active ? null : l.passageId); setMotif(''); }}
                aria-expanded={active}
                style={{ width: '100%', display: 'flex', gap: 11, flexWrap: 'wrap', alignItems: 'center', padding: '11px 14px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-strong)', minWidth: 180 }}>{l.analyte}</span>
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{l.equipement}</span>
                <span style={chip(rejet ? 'var(--red-50)' : 'var(--amber-50)', rejet ? 'var(--red-600)' : 'var(--amber-600)')}>
                  {rejet ? 'Rejet' : 'Avertissement'}
                </span>
                {l.proposition.regle && (
                  <span style={{ fontSize: 12.5, fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--text-strong)' }}>{l.proposition.regle}</span>
                )}
                {l.proposition.repete && <span style={chip('var(--red-50)', 'var(--red-600)')}>répété {l.proposition.occurrences}/{l.proposition.fenetre}</span>}
                <span style={chip('var(--cyan-50)', 'var(--cyan-700)')}>NC {l.proposition.gravite} proposée</span>
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{fmtDate(l.date)}{l.numeroSerie ? ` · série n°${l.numeroSerie}` : ''}</span>
                <I n={active ? 'check' : 'expand'} s={13} />
              </button>

              {active && (
                <div style={{ padding: '0 14px 14px', display: 'grid', gap: 11, fontSize: 12.5 }}>
                  <div style={{ background: 'var(--surface-sunken)', borderRadius: 'var(--radius-sm)', padding: '9px 12px' }}>
                    <span style={lbl}>Motif de la proposition</span>
                    <div style={{ color: 'var(--text-body)' }}>{l.proposition.motif}</div>
                  </div>

                  <div>
                    <span style={lbl}>Valeurs de la série</span>
                    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>
                      {l.valeurs.length === 0 && <span style={{ color: 'var(--text-muted)' }}>aucune valeur exploitable</span>}
                      {l.valeurs.map((v) => (
                        <span key={v.niveau} style={{ color: typeof v.z === 'number' && Math.abs(v.z) >= 2 ? 'var(--red-600)' : 'var(--text-body)' }}>
                          N{v.niveau} = {v.valeur}{typeof v.z === 'number' ? ` (z = ${v.z})` : ' (z non calculable)'}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <span style={lbl}>Règles déclenchées</span>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {l.regles.map((r) => (
                        <span key={`${r.code}-${r.niveau}`} title={r.libelle}
                          style={chip(r.gravite === 'rejet' ? 'var(--red-50)' : 'var(--amber-50)', r.gravite === 'rejet' ? 'var(--red-600)' : 'var(--amber-600)')}>
                          {r.code}{r.niveau ? ` · N${r.niveau}` : ''}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <span style={lbl}>La fenêtre qui a servi au verdict</span>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {l.historique.map((h, i) => (
                        <span key={`${h.date}-${i}`}
                          style={{ ...chip(h.statut === 'rejet' ? 'var(--red-50)' : h.statut === 'alerte' ? 'var(--amber-50)' : 'var(--green-50)',
                            h.statut === 'rejet' ? 'var(--red-600)' : h.statut === 'alerte' ? 'var(--amber-600)' : 'var(--green-600)'),
                          fontWeight: 500 }}>
                          {fmtDate(h.date)}{h.regles.length ? ` · ${h.regles.join(' ')}` : ''}
                        </span>
                      ))}
                    </div>
                    {l.lotControle && (
                      <div style={{ marginTop: 5, fontSize: 12.5, color: 'var(--text-muted)' }}>
                        Lot de contrôle en cours : <span style={{ fontFamily: 'var(--font-mono)' }}>{l.lotControle}</span>
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'grid', gap: 7 }}>
                    <label style={lbl} htmlFor={`m-${l.passageId}`}>Motif — obligatoire pour écarter</label>
                    <textarea id={`m-${l.passageId}`} value={motif} onChange={(ev) => setMotif(ev.target.value)} rows={2}
                      placeholder="Ex. contrôle repassé conforme après amorçage de l’aiguille, incident tracé sur la fiche de vie."
                      style={{ ...inputStyle, resize: 'vertical' }} />
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Bouton variante="primaire" taille="sm" onClick={() => void run('nc', () => decider(l, 'nc'))} disabled={estOccupe()}>
                        {estOccupe('nc') ? '…' : `Ouvrir une non-conformité ${l.proposition.gravite}`}
                      </Bouton>
                      <Bouton taille="sm" onClick={() => void run('ec', () => decider(l, 'ecarter'))} disabled={estOccupe() || motif.trim().length < 10} title={motif.trim().length < 10 ? 'Un motif d’au moins 10 caractères est requis : « écarté » sans raison ne se relit pas en audit.' : undefined}>
                        {estOccupe('ec') ? '…' : 'Écarter — aucune NC'}
                      </Bouton>
                      <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                        Écarter n’écrit qu’un motif et une ligne au journal : ni non-conformité, ni action, ni lien.
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <PanneauEcartees
        ouvert={panneauEcartees}
        lignes={ecartees}
        erreur={erreurEcartees}
        estRQ={estRQ}
        onBasculer={() => {
          const suivant = !panneauEcartees;
          setPanneauEcartees(suivant);
          if (suivant) void chargerEcartees();
        }}
        onReprendre={reprendre}
      />
    </div>
  );
}

/** Les propositions ÉCARTÉES — consultables, et reprenables par la RQ.
 *
 *  Replié par défaut, chargé au clic : ce n'est pas ce qu'on vient faire sur cet
 *  écran, et la lecture relit toutes les périodes ouvertes. Mais il faut que le
 *  geste soit ICI, là où l'on écarte : c'est ici qu'on s'aperçoit de l'erreur. */
function PanneauEcartees({ ouvert, lignes, erreur, estRQ, onBasculer, onReprendre }: {
  ouvert: boolean;
  lignes: LigneEcartee[] | null;
  erreur: string | null;
  estRQ: boolean;
  onBasculer: () => void;
  onReprendre: (l: LigneEcartee, motif: string) => Promise<void>;
}) {
  const { settings } = useGed();
  const [ouverte, setOuverte] = useState<string | null>(null);
  const [motif, setMotif] = useState('');
  const { run, estOccupe } = useBusy();

  return (
    <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
      <button onClick={onBasculer} aria-expanded={ouvert}
        style={{ width: '100%', display: 'flex', gap: 10, alignItems: 'center', padding: '11px 14px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
        <I n="history" s={14} />
        <strong style={{ fontSize: 13, color: 'var(--text-strong)' }}>Propositions écartées</strong>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          — déjà jugées, aucune non-conformité ouverte. Consultables ici{estRQ ? ', et reprenables' : ''}.
        </span>
        <span style={{ flex: 1 }} />
        {ouvert && lignes && (
          <span style={chip('var(--surface-sunken)', 'var(--text-muted)')}>{lignes.length}</span>
        )}
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{ouvert ? 'Masquer' : 'Afficher'}</span>
      </button>

      {ouvert && (
        <div style={{ padding: '0 14px 14px', display: 'grid', gap: 10, fontSize: 12.5 }}>
          <p style={{ margin: 0, color: 'var(--text-muted)' }}>
            Écarter n’efface rien : la décision, son auteur et son motif restent lisibles ici et
            au journal chaîné. <strong style={{ color: 'var(--text-strong)' }}>Reprendre ne les
            efface pas non plus</strong> — le refus reste au journal, seule sa conséquence est
            levée, et la reprise y est inscrite à son tour avec son propre motif.
          </p>

          {erreur && (
            <div style={{ fontSize: 12.5, color: 'var(--red-600)', borderInlineStart: '3px solid var(--red-600)', paddingInlineStart: 9 }}>
              <strong>{erreur}</strong> Ce n’est pas « aucune proposition écartée ».
            </div>
          )}
          {!erreur && !lignes && <p style={{ margin: 0, color: 'var(--text-muted)' }}>Lecture des propositions écartées…</p>}
          {!erreur && lignes?.length === 0 && (
            <p style={{ margin: 0, color: 'var(--text-muted)' }}>
              Aucune proposition écartée sur les périodes ouvertes. Une période refermée n’est
              plus relue : ce vide ne dit rien des lots clôturés.
            </p>
          )}

          {lignes?.map((l) => {
            const active = ouverte === l.passageId;
            return (
              <div key={l.passageId} style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 9 }}>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)', minWidth: 170 }}>{l.analyte}</span>
                  <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{l.equipement}</span>
                  <span style={chip(l.statut === 'rejet' ? 'var(--red-50)' : 'var(--amber-50)', l.statut === 'rejet' ? 'var(--red-600)' : 'var(--amber-600)')}>
                    {l.statut === 'rejet' ? 'Rejet' : 'Avertissement'}
                  </span>
                  <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                    passage du {fmtDate(l.date)}{l.numeroSerie ? ` · série n°${l.numeroSerie}` : ''}
                  </span>
                  <span style={{ flex: 1 }} />
                  {estRQ && (
                    <Bouton taille="sm" onClick={() => { setOuverte(active ? null : l.passageId); setMotif(''); }} aria-expanded={active}>
                      <I n="refresh" s={12} />{active ? 'Annuler' : 'Reprendre'}
                    </Bouton>
                  )}
                </div>
                <div style={{ marginTop: 4, color: 'var(--text-body)' }}>
                  <span style={lbl}>Écartée{l.ecartement.par ? ` par ${userName(settings, l.ecartement.par)}` : ''}
                    {l.ecartement.le ? ` le ${fmtDate(l.ecartement.le)}` : ''}</span>
                  <div>{l.ecartement.motif || <em style={{ color: 'var(--text-muted)' }}>motif non enregistré (décision antérieure à l’obligation de motif)</em>}</div>
                </div>
                {l.historique.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 5 }}>
                    {l.historique.map((h, i) => (
                      <span key={`${h.date}-${i}`}
                        style={{ ...chip(h.statut === 'rejet' ? 'var(--red-50)' : h.statut === 'alerte' ? 'var(--amber-50)' : 'var(--green-50)',
                          h.statut === 'rejet' ? 'var(--red-600)' : h.statut === 'alerte' ? 'var(--amber-600)' : 'var(--green-600)'),
                        fontWeight: 500 }}>
                        {fmtDate(h.date)}{h.regles.length ? ` · ${h.regles.join(' ')}` : ''}
                      </span>
                    ))}
                  </div>
                )}

                {active && estRQ && (
                  <div style={{ display: 'grid', gap: 7, marginTop: 9 }}>
                    <label style={lbl} htmlFor={`r-${l.passageId}`}>Motif — obligatoire pour reprendre</label>
                    <textarea id={`r-${l.passageId}`} value={motif} onChange={(ev) => setMotif(ev.target.value)} rows={2}
                      placeholder="Ex. écartée par erreur de ligne : c’est le passage du lendemain qui avait été repassé, pas celui-ci."
                      style={{ ...inputStyle, resize: 'vertical' }} />
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Bouton variante="primaire" taille="sm" onClick={() => void run(`rp-${l.passageId}`, async () => {
                        await onReprendre(l, motif); setOuverte(null); setMotif('');
                      })} disabled={estOccupe() || motif.trim().length < 10} title={motif.trim().length < 10 ? 'Un motif d’au moins 10 caractères est requis : reprendre revient sur une décision signée.' : undefined}>
                        {estOccupe(`rp-${l.passageId}`) ? '…' : 'Remettre à l’examen'}
                      </Bouton>
                      <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                        La proposition redevient à trancher si la règle en vigueur la propose
                        encore. Aucune non-conformité n’est créée par ce geste.
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Le réglage du déclenchement. RQ seul : il change ce que le registre des
 *  non-conformités contiendra, ce n'est pas une préférence d'affichage. */
function Reglages({ params, defauts, catalogue, onEnregistre }: {
  params: ParamsNc; defauts: ParamsNc;
  catalogue: { code: string; libelle?: string; gravite: string }[];
  onEnregistre: () => Promise<void>;
}) {
  const { showToast } = useGed();
  const [f, setF] = useState<ParamsNc>(params);
  const { run, estOccupe } = useBusy();

  const enregistrer = async () => {
    const r = await fetchEcriture('/api/ged/ged/cqi-nc/params', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(f),
    });
    const d = await r.json().catch(() => null);
    if (!r.ok) { showToast(d?.error?.message || 'Refusé par le moteur.', 'erreur'); return; }
    showToast('Règle de déclenchement enregistrée — les propositions sont recalculées.');
    await onEnregistre();
  };

  return (
    <div style={{ ...card, padding: 14, display: 'grid', gap: 12, fontSize: 12.5 }}>
      <p style={{ margin: 0, color: 'var(--text-muted)' }}>
        Ces valeurs décident de ce qui remonte. Elles ne sont pas une constante du logiciel :
        les défauts livrés — proposer sur tout rejet, sur une alerte seulement si elle se
        répète, dans un horizon de 30 jours et uniquement sur ce qui n’a pas reçu de suite —
        sont un point de départ défendable, à ajuster sur la réalité du plateau. Sans les deux
        dernières bornes, la reprise du tableur produisait 1 284 propositions : le noyage que
        ce dispositif existe précisément pour éviter.
      </p>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input type="checkbox" checked={f.actif} onChange={(e) => setF({ ...f, actif: e.target.checked })} />
        <span>Proposer des non-conformités depuis le contrôle interne</span>
      </label>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 12 }}>
        <div>
          <label style={lbl} htmlFor="p-rej">Sur un passage REJETÉ</label>
          <Selecteur id="p-rej" value={f.surRejet} onChange={(v) => setF({ ...f, surRejet: v as ParamsNc['surRejet'] })} style={inputStyle}>
            {REACTIONS.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
          </Selecteur>
        </div>
        <div>
          <label style={lbl} htmlFor="p-ale">Sur un passage en ALERTE</label>
          <Selecteur id="p-ale" value={f.surAlerte} onChange={(v) => setF({ ...f, surAlerte: v as ParamsNc['surAlerte'] })} style={inputStyle}>
            {REACTIONS.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
          </Selecteur>
        </div>
        <div>
          <label style={lbl} htmlFor="p-occ">Passages non conformes exigés</label>
          <input id="p-occ" type="number" min={2} max={100} value={f.occurrencesRepetition}
            onChange={(e) => setF({ ...f, occurrencesRepetition: Number(e.target.value) })} style={inputStyle} />
        </div>
        <div>
          <label style={lbl} htmlFor="p-fen">…sur combien de derniers passages</label>
          <input id="p-fen" type="number" min={2} max={100} value={f.fenetreRepetition}
            onChange={(e) => setF({ ...f, fenetreRepetition: Number(e.target.value) })} style={inputStyle} />
        </div>
        <div>
          <label style={lbl} htmlFor="p-hor">Horizon, en jours (0 = aucun)</label>
          <input id="p-hor" type="number" min={0} max={3650} value={f.horizonJours}
            onChange={(e) => setF({ ...f, horizonJours: Number(e.target.value) })} style={inputStyle} />
        </div>
        <div>
          <label style={lbl} htmlFor="p-gi">Gravité proposée — situation isolée</label>
          <Selecteur id="p-gi" value={f.graviteIsolee} onChange={(v) => setF({ ...f, graviteIsolee: v as ParamsNc['graviteIsolee'] })} style={inputStyle}>
            {GRAVITES.map((g) => <option key={g} value={g}>{g}</option>)}
          </Selecteur>
        </div>
        <div>
          <label style={lbl} htmlFor="p-gr">Gravité proposée — situation répétée</label>
          <Selecteur id="p-gr" value={f.graviteRepetee} onChange={(v) => setF({ ...f, graviteRepetee: v as ParamsNc['graviteRepetee'] })} style={inputStyle}>
            {GRAVITES.map((g) => <option key={g} value={g}>{g}</option>)}
          </Selecteur>
        </div>
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input type="checkbox" checked={f.seulementSansSuite}
          onChange={(e) => setF({ ...f, seulementSansSuite: e.target.checked })} />
        <span>Ne proposer que les passages sans suite — un rejet déjà dérogé ou repassé a eu son traitement</span>
      </label>

      <div>
        <span style={lbl}>Règles écartées du déclenchement</span>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {catalogue.map((r) => (
            <label key={r.code} style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>
              <input type="checkbox" checked={f.reglesExclues.includes(r.code)}
                onChange={(e) => setF({
                  ...f,
                  reglesExclues: e.target.checked
                    ? [...f.reglesExclues, r.code]
                    : f.reglesExclues.filter((c) => c !== r.code),
                })} />
              <span title={r.libelle}>{r.code}</span>
            </label>
          ))}
        </div>
        <span style={{ display: 'block', marginTop: 4, fontSize: 12.5, color: 'var(--text-muted)' }}>
          Une règle écartée ne propose plus RIEN et ne compte plus dans la répétition — elle
          continue d’être évaluée et tracée sur la carte, elle cesse seulement d’alimenter le
          registre des non-conformités.
        </span>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <Bouton variante="primaire" taille="sm" onClick={() => void run('sv', enregistrer)} disabled={estOccupe()}>
          {estOccupe('sv') ? '…' : 'Enregistrer la règle'}
        </Bouton>
        <Bouton taille="sm" onClick={() => setF(defauts)}>Revenir aux valeurs livrées</Bouton>
      </div>
    </div>
  );
}
